import { ref } from 'vue';
import { useCookies } from '@vueuse/integrations/useCookies'
import { NewSparkData } from '@/services/idb.js';
import axios from "axios";
import { sendGraphQLQuery } from '@/services/gql-util';
import type { Member } from '@/types/Member'; // update the path to where Member is defined

type UpdateUserInfoInput = Partial<Omit<Member, 'id' | 'user' | 'created' | 'lastlogin' | 'publicfiles' | 'unmoderatedfiles' | 'deniedfiles' | 'approvedfiles' | 'friends' | 'followers' | 'following' | 'permissions'>>;

export async function getUserInfo(userId: number, vhost: number): Promise<Member> {
  const query = `
  query ($id: Int!) {
    Users {
      user(id: $id) {
        id user firstname lastname email phone city state country postalcode
        description birthdate website address1 address2 active disabled meta
        created lastlogin publicfiles unmoderatedfiles deniedfiles approvedfiles
      }
    }
  }`;
  const data = await sendGraphQLQuery<{ Users: { user: Member } }>(query, { id: userId });
  return data.Users.user;
}

export async function getUsers(vhost: number, start: number = 0, limit: number = 25, query?: string, sort?: string): Promise<{ users: Member[]; totalCount: number }> {
  const filters: any[] = [];
  if (query) {
    filters.push({ field: 'query', value: query });
  }
  const gql = `
  query ($vhost: Int!, $start: Int, $limit: Int, $filters: [UserFilters], $sort: String) {
    Users {
      userList(vhost: $vhost, start: $start, limit: $limit, filters: $filters, sort: $sort) {
        items {
          id user firstname lastname email phone active disabled created lastlogin
          city country publicfiles approvedfiles unmoderatedfiles deniedfiles
        }
        totalCount
      }
    }
  }`;
  const data = await sendGraphQLQuery<{ Users: { userList: { items: Member[]; totalCount: number } } }>(gql, { vhost, start, limit, filters, sort: sort || 'created DESC' });
  return { users: data.Users.userList.items, totalCount: data.Users.userList.totalCount };
}

export async function getUserActivity(userId: number, vhost: number): Promise<any[]> {
  const query = `
  query ($vhost: Int!, $start: Int, $limit: Int) {
    Activity {
      activityList(vhost: $vhost, userOnly: true, start: $start, limit: $limit) {
        id type message created
      }
    }
  }`;
  const data = await sendGraphQLQuery<{ Activity: { activityList: any[] } }>(query, { vhost, start: 0, limit: 50 });
  return data?.Activity?.activityList || [];
}

export async function updateUser(
  userIdOrInfo: string | UpdateUserInfoInput,
  userInfo?: UpdateUserInfoInput
): Promise<Member> {
  // Determine parameters based on input
  let userId: string | undefined;
  let actualUserInfo: UpdateUserInfoInput;
  
  if (typeof userIdOrInfo === 'string') {
    // First argument is userId, second is userInfo
    userId = userIdOrInfo;
    actualUserInfo = userInfo!;
  } else {
    // Only userInfo was provided (update current user)
    actualUserInfo = userIdOrInfo;
  }

  const mutation = `
  mutation ($userId: String, $userInfo: UserInfoInput!)  { UserMutations { 
								       updateUserInfo(userId: $userId, userInfo: $userInfo) {
									       id
									       user
									       firstname
									       lastname
									       email
									       phone
									       city
									       state
									       country
									       postalcode
									       description
									       birthdate
									       website
									       address1
									       address2
									       active
									       disabled
									       meta
									       created
								       }
  } }
  `;

  const variables = { 
    userId: userId || null, // Send null if userId is not provided
    userInfo: actualUserInfo
  };

  const data = await sendGraphQLQuery<{ updateUserInfo: Member }>(mutation, variables);
  return data.updateUserInfo;
}

export function useFaceIDAuthentication(storedUser: NewSparkData) {
	const isAuthenticated = ref(false);

	const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
		const binaryString = atob(base64);
		const len = binaryString.length;
		const bytes = new Uint8Array(len);
		for (let i = 0; i < len; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		return bytes.buffer;
	};

	const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
		const bytes = new Uint8Array(buffer);
		let binary = '';
		for (let i = 0; i < bytes.byteLength; i++) {
			binary += String.fromCharCode(bytes[i]);
		}
		return btoa(binary);
	};

	const authenticateWithFaceID = async (): Promise<void> => {
		try {
			const res = await fetch(`/services/auth/authentication-options`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ userId: storedUser.dbId }),
			});

			const data = await res.json();
			if (!data.publicKey) {
				console.log('Face ID not set up.');
				return;
			}

			const challenge = base64ToArrayBuffer(data.publicKey.challenge);
			const allowCredentials = data.publicKey.allowCredentials.map((cred: any) => ({
				id: base64ToArrayBuffer(cred.id),
				type: cred.type,
				transports: cred.transports || ['usb', 'ble', 'nfc', 'internal'],
			}));

			const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
				challenge,
				rpId: data.publicKey.rpId,
				allowCredentials,
				userVerification: data.publicKey.userVerification || 'preferred',
				timeout: data.publicKey.timeout || 60000,
			};

			const credential = await navigator.credentials.get({ publicKey: publicKeyCredentialRequestOptions }) as PublicKeyCredential;

			if (!credential) {
				console.error('No credential received.');
				return;
			}

			const credentialForRequest = {
				id: credential.id,
				rawId: arrayBufferToBase64(credential.rawId),
				type: credential.type,
				authenticatorAttachment: credential.authenticatorAttachment,
				response: {
					authenticatorData: arrayBufferToBase64((credential.response as AuthenticatorAssertionResponse).authenticatorData),
					clientDataJSON: arrayBufferToBase64((credential.response as AuthenticatorAssertionResponse).clientDataJSON),
					signature: arrayBufferToBase64((credential.response as AuthenticatorAssertionResponse).signature),
					userHandle: (credential.response as AuthenticatorAssertionResponse).userHandle ? arrayBufferToBase64((credential.response as AuthenticatorAssertionResponse).userHandle!) : null,
				},
			};

			const response = await fetch(`/services/auth/authenticate`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				credentials: 'include',
				body: JSON.stringify({ userId: storedUser.dbId, credential: credentialForRequest }),
			});

			const result = await response.json();
			console.log(result);
			if (result.status === 'success') {
				isAuthenticated.value = true;
				const userInfo = result.user;
			} else {
				console.log('Face ID login failed.');
			}
		} catch (error) {
			console.error('Error authenticating with Face ID:', error);
		}
	};

	const removeFaceID = async (): Promise<boolean> => {
		try {
			const response = await fetch(`/services/auth/remove`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			const { success } = await response.json();
			if (success) {
				storedUser.faceId = 0;
				return true;
			} else {

				console.log('Failed to remove Face ID.');
				return false;
			}
		} catch (error) {
			console.error('Error removing Face ID:', error);
		}
		return false;
	};
	const registerFaceID = async (): Promise<boolean> => {
		try {
			const response = await fetch(`/services/auth/register-options`, {
				credentials: 'include',
			});
			const options = await response.json();

			if (!options.publicKey) {
				throw new Error('Invalid response from server');
			}

			const credential = await navigator.credentials.create({
				publicKey: {
					...options.publicKey,
					challenge: base64ToArrayBuffer(options.publicKey.challenge),
					user: {
						...options.publicKey.user,
						id: base64ToArrayBuffer(options.publicKey.user.id),
					},
				},
			}) as PublicKeyCredential;

			if (!credential) {
				throw new Error('Failed to create credentials');
			}

			const registerResponse = await fetch(`/services/auth/register`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					clientDataJSON: arrayBufferToBase64((credential.response as AuthenticatorAttestationResponse).clientDataJSON),
					attestationObject: arrayBufferToBase64((credential.response as AuthenticatorAttestationResponse).attestationObject),
				}),
			});

			const result = await registerResponse.json();
			return true;
			if (result.status === 'success') {
				storedUser.faceId = 1;
				return true;
			}
			throw new Error(result.message);
		} catch (error) {
			console.error('Registration failed:', error);
		}
		return false;
	};

	return { isAuthenticated, authenticateWithFaceID, removeFaceID, registerFaceID,getLoginMethod};
}
export async function getLoginMethod(newEmail:string):Promise<any> {
	const query = ` query getLoginMethod($userName: String!, $vhost: Int!) {Users  { getLoginMethod(userName: $userName, vhost: $vhost) {   status      id  } } }  `;

	const variables = { userName: newEmail, vhost: 2, };

	try {
		const response = await axios.post('/gql', {
			query,
			variables,
		});

		if (response.data.data.Users.getLoginMethod.status == "true") {
			return {id:response.data.data.Users.getLoginMethod.id,status:'success'};
		} else {
			return {id:0,status:response.data.data.Users.getLoginMethod.status};
		}
	} catch (error) {
		console.error('API call failed:', error);
		return {id:0,status:error};
	}
	return false;
}

